import React, { useEffect, useRef, useState } from "react";
import {
  Close,
  Keyboard,
  KeyboardVoice,
  RestartAlt,
  ThumbUpAltOutlined,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import CustomTextField from "./CustomTextField";
import Button from "./Button";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { tokens } from "../../theme";
import { motion, AnimatePresence } from "framer-motion";
import CustomErrorAlert from "./CustomErrorAlert";
import { useModalStyles } from "./useModalStyles";

interface SpeechToTextProps {
  open: boolean;
  onClose: () => void;
  onSubmitVoice: (data: any) => void;
}

const SpeechToTextPopup: React.FC<SpeechToTextProps> = ({
  open,
  onClose,
  onSubmitVoice,
}) => {
  const { control, handleSubmit, setValue, getValues } = useForm();
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    finalTranscript,
  } = useSpeechRecognition();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const colors = tokens(theme.palette.mode);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [volBars, setVolBars] = useState([0, 0, 0, 0, 0]);
  const [shouldClose, setShouldClose] = useState(false);
  // CS-1426 - Start
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // CS-1426 - End

  const analyserRef = useRef<{
    stream: MediaStream;
    ctx: AudioContext;
    analyser: AnalyserNode;
  } | null>(null);

  const rafRef = useRef<number | null>(null);

  const createVoiceAnalyser = async () => {
    try { // CS-1426
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    const source = ctx.createMediaStreamSource(stream);
    source.connect(analyser);
    return { stream, ctx, analyser };
    // CS-1426 - Start
    } catch (err) {
      setErrorMsg(
        "Microphone access was denied.\n\n" +
        "To use speech-to-text, please:\n" +
        "1. Open your browser’s site settings.\n" +
        "2. Allow microphone access for this site.\n" +
        "3. Reload the page and try again."
      );
      setErrorOpen(true);
      return null;
    }
  };

  const startVolumeMeter = (analyserObj: { stream: MediaStream; ctx: AudioContext; analyser: AnalyserNode }) => {
    analyserRef.current = analyserObj;
    // CS-1426 - End
    const data = new Uint8Array(analyserRef.current.analyser.frequencyBinCount);

    const loop = () => {
      if (!analyserRef.current) return;
      analyserRef.current.analyser.getByteFrequencyData(data);
      const vol = data.reduce((a, b) => a + b, 0) / data.length / 2.55;
      const bars = Array.from({ length: 9 }, (_, i) =>
        Math.min(40, Math.max(4, vol - i * 4.5))
      );

      setVolBars(bars);
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const stopVolumeMeter = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    analyserRef.current?.stream.getTracks().forEach((t) => t.stop());
    analyserRef.current?.ctx.close();
    analyserRef.current = null;
    setVolBars([0, 0, 0, 0, 0]);
  };

  useEffect(()=>{
    setValue("speechtext", "");
  },[])

  useEffect(() => {
    setValue("speechtext", transcript);
  }, [transcript, setValue]);

  useEffect(() => {
    if (!listening) { // CS-1426
      stopVolumeMeter();
    }
  }, [listening]);

  // CS-1426 - Start
  const handleMicButtonClick = async () => {
    if (listening) {
      SpeechRecognition.stopListening();
      return;
    }

    const analyser = await createVoiceAnalyser();
    if (!analyser) {
      return;
    }

    SpeechRecognition.startListening({
      continuous: true,
      language: "en-IN",
    });
    startVolumeMeter(analyser);
  };
  // CS-1426 - End

  const onSubmit = async () => {
    SpeechRecognition.abortListening();
    stopVolumeMeter();
    onSubmitVoice(getValues("speechtext"));
    onClose();
  };

  useEffect(()=>{
    if(shouldClose){
      onClose();
    }
  },[shouldClose])

  useEffect(() => {
    if (open) {
      setShouldClose(false);
    }
  }, [open]);


  return (
    <> {/* CS-1426 - Start */}
    <Dialog
      open={open}
      keepMounted
      onClose={onClose}
      aria-describedby="speech-to-text-dialog-description"
      onClick={(event) => event.stopPropagation()}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(modalTokens.accent)}>
            <KeyboardVoice sx={{ fontSize: "1.2rem", color: modalTokens.accent }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
              Speech to Text
            </Typography>
            <Typography id="speech-to-text-dialog-description" sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
              Speak or type text, then confirm to continue.
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={()=>{
              SpeechRecognition.abortListening();
              resetTranscript();
              setShouldClose(true);
            }}
            size="small"
            sx={closeIconSx}
          >
            <Close />
          </IconButton>
        </Box>
        <Box sx={bodySx}>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    height: 100,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      width: 160,
                      height: 160,
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 1,
                    }}
                  >
                    {listening &&
                      [0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{
                            scale: [0.4, 0.7],
                            opacity: [0, 0.1, 0],
                          }}
                          transition={{
                            duration: 2.8,
                            repeat: Infinity,
                            delay: i * 1,
                            ease: "easeInOut",
                          }}
                          style={{
                            position: "absolute",
                            width: 160,
                            height: 160,
                            borderRadius: "50%",
                            backgroundColor: colors.error[300],
                            left: 0,
                            top: 0,
                          }}
                        />
                      ))}
                  </Box>

                  <AnimatePresence>
                    {listening && volBars.some((h) => h > 4) && (
                      <>
                        <motion.div
                          key="left-wave"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          style={{
                            position: "absolute",
                            left: "calc(50% - 60px)",
                            transform: "translateX(-100%)",
                            display: "flex",
                            flexDirection: "row-reverse",
                            gap: 4,
                            alignItems: "center",
                            height: 40,
                          }}
                        >
                          {volBars.map((h, i) => (
                            <motion.div
                              key={`l-${i}`}
                              animate={{ height: h }}
                              transition={{ duration: 0.08 }}
                              style={{
                                width: 4,
                                height: h,
                                background: `linear-gradient(180deg, ${colors.error[200]}, ${colors.error[500]})`,
                                borderRadius: 2,
                              }}
                            />
                          ))}
                        </motion.div>

                        <motion.div
                          key="right-wave"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          style={{
                            position: "absolute",
                            right: "calc(50% - 60px)",
                            transform: "translateX(100%)",
                            display: "flex",
                            gap: 4,
                            alignItems: "center",
                            height: 40,
                          }}
                        >
                          {volBars.map((h, i) => (
                            <motion.div
                              key={`r-${i}`}
                              animate={{ height: h }}
                              transition={{ duration: 0.08 }}
                              style={{
                                width: 4,
                                height: h,
                                background: `linear-gradient(180deg, ${colors.error[200]}, ${colors.error[500]})`,
                                borderRadius: 2,
                              }}
                            />
                          ))}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>

                  {!listening && (
                    <Tooltip title="Clear Text">
                      <motion.div
                        key="reset"
                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          position: "absolute",
                          left: "calc(50% - 100px)",
                          transform: "translateX(-50%)",
                          zIndex: 3,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setValue("speechtext", "");
                          resetTranscript();
                        }}
                      >
                        <RestartAlt />
                      </motion.div>
                    </Tooltip>
                  )}

                  <IconButton
                    onClick={handleMicButtonClick} // CS-1426
                    sx={{
                      width: 80,
                      height: 80,
                      zIndex: 5,
                      bgcolor: listening ? "#ff1744" : "#1976d2",
                      color: "white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      transition: "background-color 0.4s ease",
                      "&:hover": {
                        bgcolor: listening ? "#d50000" : "#1565c0",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <motion.div
                        animate={{ rotateY: listening ? 180 : 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transformStyle: "preserve-3d",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            backfaceVisibility: "hidden",
                            position: "absolute",
                          }}
                        >
                          <KeyboardVoice />
                        </Box>
                        <Box
                          sx={{
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                            position: "absolute",
                          }}
                        >
                          <Close />
                        </Box>
                      </motion.div>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.75rem",
                          mt: 2,
                          color: "white",
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        {listening ? "Stop" : "Start"}
                      </Typography>
                    </Box>
                  </IconButton>
                  
                  {!listening && (
                    <Tooltip title="Type with Keyboard">
                      <motion.div
                        key="keyboard"
                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          position: "absolute",
                          left: "calc(50% + 100px)",
                          transform: "translateX(-50%)",
                          zIndex: 3,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          inputRef.current?.focus();
                        }}
                      >
                        <Keyboard />
                      </motion.div>
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: modalTokens.textSecondary, mb: 0.7 }}>
                  Text
                </Typography>
                <Box sx={inputFieldSx(false)}>
                  <CustomTextField
                    control={control}
                    controllerName="speechtext"
                    label=""
                    multiline
                    rows={3}
                    ref={inputRef}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="submit"
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
                order: { xs: 1, sm: 2 },
              }}
            >
              <ThumbUpAltOutlined sx={{ fontSize: "1.1rem" }} />
              Confirm
            </Box>
            <Box
              component="button"
              type="button"
              onClick={()=>{
                SpeechRecognition.abortListening();
                resetTranscript();
                setShouldClose(true);
              }}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </form>
    </Dialog>
    {/* CS-1426 - Start */}
      <CustomErrorAlert
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        severity="error"
        mainErrorObj={{ popupType: "Dialog", title: "Microphone Permission Denied" } as any}
        content={errorMsg}
        alertTitle="Microphone Permission Denied"
        sx={{
          zIndex: 2000,
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          "& .MuiDialog-paper": {
            maxWidth: 600,
            minWidth: 350,
            "@media (max-width:600px)": {
              maxWidth: "90%",
              minWidth: "unset",
              mt: 2,
            },
          },
        }}
      />
    </>
    // CS-1426 - End
  );
};

export default SpeechToTextPopup;
